import { useRef, useState, useEffect } from "react";

export const useCarouselScroll = <T extends HTMLElement = HTMLDivElement>() => {
  const ref = useRef<T>(null);
  const [isShowPrevButton, setIsShowPrevButton] = useState(false);
  const [isShowNextButton, setIsShowNextButton] = useState(false);

  /** 左のボタン（ArrowLeft）押下時に発火する関数 */
  const onLeftBtnClicked = () => {
    const targetRef = ref.current;
    if (targetRef === null) return;

    const { scrollLeft, clientWidth } = targetRef;

    if (scrollLeft === 0) return;

    targetRef.scroll({
      top: 0,
      left: scrollLeft - clientWidth,
      behavior: "smooth",
    });
  };

  /** 右のボタン（AllowRight）押下時に発火する関数 */
  const onRightBtnClicked = () => {
    const targetRef = ref.current;
    if (targetRef === null) return;

    const { scrollLeft, scrollWidth, clientWidth } = targetRef;

    if (scrollWidth - Math.ceil(scrollLeft) === clientWidth) return;

    targetRef.scroll({
      top: 0,
      left: scrollLeft + clientWidth,
      behavior: "smooth",
    });
  };

  const scrollLeftListener = () => {
    const targetRef = ref.current;
    if (targetRef === null) return;

    requestAnimationFrame(() => {
      const { scrollLeft, scrollWidth, clientWidth } = targetRef;

      setIsShowPrevButton(scrollLeft !== 0);

      const isDisplayFinalElement = scrollWidth - Math.ceil(scrollLeft) === clientWidth;

      setIsShowNextButton(!isDisplayFinalElement);
    });
  };

  useEffect(() => {
    const targetRef = ref.current;
    if (targetRef === null) return;

    const { scrollWidth, clientWidth } = targetRef;
    setIsShowNextButton(scrollWidth > clientWidth);

    targetRef.addEventListener("scroll", scrollLeftListener, { passive: true });

    return () => {
      targetRef.removeEventListener("scroll", scrollLeftListener);
    };
  }, []);

  useEffect(() => {
    (async () => {
      if (!("scrollBehavior" in document.documentElement.style)) {
        await import("scroll-behavior-polyfill");
      }
    })();
  }, []);

  return {
    ref,
    isShowPrevButton,
    isShowNextButton,
    onLeftBtnClicked,
    onRightBtnClicked,
  };
};
